import React, { useState, ChangeEvent, ReactElement } from 'react';
import { Loader, NavbarDefault, SupportModal } from 'app/components';
import { i18n } from 'app/utils/i18n';
import { useImportedTaxonomies, useAvailableTaxonomyFolders, useImportTaxonomy, taxonomyQueryKeys } from 'app/api/tag-queries/taxonomy';
import { useQueryClient } from 'react-query';
import moment from 'moment';

const Taxonomies = (): ReactElement => {
  const { data: taxonomies, isLoading } = useImportedTaxonomies();
  const { data: availableFolders, isLoading: isFoldersLoading } = useAvailableTaxonomyFolders();
  const importTaxonomy = useImportTaxonomy();
  const queryClient = useQueryClient();

  const [selectedFolder, setSelectedFolder] = useState<string>('');

  const allFolders = availableFolders ?? [];

  function handleImport(): void {
    if (!selectedFolder) return;

    importTaxonomy.mutate(selectedFolder, {
      onSuccess: () => {
        setSelectedFolder('');
        queryClient.invalidateQueries(taxonomyQueryKeys.all);
        queryClient.invalidateQueries(taxonomyQueryKeys.availableFolders);
      },
    });
  }

  function renderContent(): ReactElement {
    if (isLoading || !taxonomies) {
      return <Loader />;
    }

    if (taxonomies.length === 0) {
      return <p className="text-muted mt-3">{i18n('taxonomy.admin.empty')}</p>;
    }

    return (
      <table className="mt-3">
        <thead>
          <tr>
            <th>{i18n('taxonomy.admin.name')}</th>
            <th>{i18n('taxonomy.admin.imported-date')}</th>
          </tr>
        </thead>
        <tbody>
          {taxonomies.map((taxonomy) => (
            <tr key={taxonomy.id}>
              <td>
                {taxonomy.name}
                {taxonomy.needsReimport && (
                  <span className="badge bg-warning text-dark ms-2" title={i18n('taxonomy.admin.needs-reimport-tooltip')}>
                    {i18n('taxonomy.admin.needs-reimport')}
                  </span>
                )}
              </td>
              <td>{moment(taxonomy.createdAt).format('YYYY-MM-DD HH:mm')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  return (
    <div>
      <div className="grid--container full--height columns">
        <div className="content">
          <div className="content-title columns two mb-2">
            <h1>{i18n('taxonomy.admin.title')}</h1>
            <div className="justify-self-end d-flex align-items-center gap-2">
              <select
                className="form-select"
                value={selectedFolder}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => setSelectedFolder(e.target.value)}
                disabled={isFoldersLoading}
              >
                <option value="">{i18n('taxonomy.admin.select-folder')}</option>
                {allFolders.map((f) => (
                  <option
                    key={f.folder}
                    value={f.folder}
                    disabled={!f.hasRequiredFiles}
                    title={f.hasRequiredFiles ? undefined : `${i18n('taxonomy.admin.missing-files')}: ${(f.missingFiles ?? []).join(', ')}`}
                  >
                    {f.folder}
                    {!f.hasRequiredFiles ? ` — ${i18n('taxonomy.admin.missing-files')}` : ''}
                  </option>
                ))}
              </select>
              <button className="btn btn-primary text-nowrap" onClick={handleImport} disabled={!selectedFolder || importTaxonomy.isLoading}>
                {importTaxonomy.isLoading ? i18n('taxonomy.admin.importing') : i18n('taxonomy.admin.import')}
              </button>
            </div>
          </div>
          {importTaxonomy.isError && <div className="alert alert-danger mt-2">{i18n('taxonomy.admin.import-error')}</div>}
          {importTaxonomy.isSuccess && <div className="alert alert-success mt-2">{i18n('taxonomy.admin.import-success')}</div>}
          {renderContent()}
        </div>
      </div>
      <NavbarDefault />
      <SupportModal bottomOffset={0} />
    </div>
  );
};

export default Taxonomies;
